import React, { CSSProperties, useState, useEffect, useRef } from "react";
import { Header, Image, Segment } from "semantic-ui-react";
import IconTab from "./IconTab";
import ProfileSection from "./ProfileSection";
import FormSection from "./FormSection";
import BookingSection from "./BookingSection";
import NoteSection from "./NoteSection";
import CartSection from "./CartSection";

// Serializers
import PatientQueueSerializer from "issara-sdk/types/PatientQueueSerializer_apps_PRX";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import DiagFormSerializer from "issara-sdk/types/DiagFormSerializer_apps_PRX";
import ProxyPatientSerializer from "issara-sdk/types/ProxyPatientSerializer_apps_PRX";
import PatientNoteSerializer from "issara-sdk/types/PatientNoteSerializer_apps_REG";

// Interface
import { CartData, CartPackageListCard, CartPaymentLinkCard, DiagFormData, DiagFormFilter } from "./StaffChatInterface";

type ProfilePanelProps = {
  onEvent: (e: any) => any;
  setProp?: (key: string, value: any, callback?: any) => any;
  selectedChat?: PatientQueueSerializer;
  selectedProxyPatient?: ProxyPatientSerializer;
  patientData?: PatientSerializer;
  activeTabs: TabType[];
  forms?: DiagFormSerializer[];
  appointments?: PatientAppointmentSerializer[];
  officialAccountList?: any;
  notes?: PatientNoteSerializer[];
  diagFormData: DiagFormData;
  onSearchForm: (filter: DiagFormFilter, activePage: number) => void;
  onSaveNote: (note: string) => void;
  onUpdateNote: (id: number, note: string) => void;
  onDeleteNote: (id: number) => void;
  onToggleTab: (tab: TabType) => void;
  apiToken?: string;
  // --- สำหรับ BookingSection (SelectDatetime flow) ---
  createAppointment?: any;
  timeDivision?: any[];
  loadingStatus?: Record<string, any>;
  errorMessage?: any;
  createdAppointment?: any;
  // --- สำหรับ CartSection (ค้นหา/เพิ่มรายการตรวจของผู้รับบริการ) ---
  cartData?: CartData;
  // ส่งลิงก์ชำระเงิน (mehealthCheckout) เข้าแชทของลูกค้า — StaffChat เป็นคน post เข้า chat channel
  onSendCartPaymentLink?: () => Promise<any>;
};

type TabType = "profile" | "form" | "booking" | "note" | "cart";

const styles = {
  mainSegment: {
    width: "325px",
    borderLeft: "1px solid #CCCCCC",
    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    borderRadius: 0,
    margin: 0,
    // height: "100%",
    height: "calc(100vh - 2.5rem)",
    overflowY: "auto",
    padding: "20px 0",
  } as CSSProperties,
  center: { textAlign: "center" } as CSSProperties,
  profileImage: { width: "62px", height: "62px", marginBottom: "1rem" } as CSSProperties,
  noMargin: { margin: 0 } as CSSProperties,
  iconTabsContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: "20px",
    marginBottom: "20px",
    padding: "0 2rem",
  } as CSSProperties,
};

const ProfilePanel: React.FC<ProfilePanelProps> = (props) => {
  // const [activeTabs, setActiveTabs] = useState<TabType[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    const patient = props.selectedChat?.patient;
    const division = props.selectedChat?.division_id ?? props.selectedChat?.division;

    if (patient) {
      props.onEvent({ message: "GetPatientChatDetail", params: { patient } });
      props.onEvent({ message: "GetPatientAppointmentList", params: { patient, division } });
      props.onEvent({ message: "GetPatientDiagFormList", params: { patient, division } });
      props.onEvent({ message: "GetPatientNoteList", params: { patient } });
      props.onEvent({ message: "GetProxyPatient", params: { patient } });
      props.onEvent({
      message: "GetOfficialAccountList",
        params: { search: "", card: "GET_LIST_OFFICIAL_ACCOUNT" },
      });
      // จำนวนในตะกร้า (PackageOrderWithItemList.get) สำหรับ badgeCount ของ tab Cart
      props.onEvent({ message: "GetCartPackageCount", params: { patient } });
    }
  }, [props.selectedChat?.patient, props.selectedChat?.division_id]);

  // tab Cart — CartSection เป็นผู้ยิงค้นเอง (debounce ในตัวมันเอง) effect นี้จึงแค่
  // ล้าง loading card เมื่อปิด tab กัน spinner ค้างกรณี request ยังไม่จบ
  useEffect(() => {
    if (props.activeTabs.includes("cart")) return;

    if (props.loadingStatus?.[CartPackageListCard]) {
      props.setProp?.(`loadingStatus.${CartPackageListCard}`, false);
    }
  }, [props.activeTabs, props.loadingStatus?.[CartPackageListCard]]);

  // const toggleTab = (tab: TabType) => {
  //   setActiveTabs((prev) => {
  //     return prev.includes(tab) ? prev.filter((t) => t !== tab) : [...prev, tab];
  //   });
  // };

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Segment style={styles.mainSegment}>
      {/* Profile Section */}
      <div style={styles.center}>
        <Image
          src={props.selectedChat?.image || "/static/images/person.png"}
          circular
          centered
          style={styles.profileImage}
        />
        <Header as="h4" style={styles.noMargin}>
          {props.patientData?.first_name} {props.patientData?.last_name}
        </Header>
      </div>

      {/* Icon Tabs */}
      <div style={styles.iconTabsContainer}>
        {/* Profile Icon */}
        <IconTab
          iconName="user"
          label="Profile"
          // isActive={activeTabs.includes("profile")}
          // onClick={() => toggleTab("profile")}
          isActive={props.activeTabs.includes("profile")}
          onClick={() => props.onToggleTab("profile")}
        />

        {/* Form Icon */}
        <IconTab
          iconName="file alternate"
          label="Form"
          // isActive={activeTabs.includes("form")}
          // onClick={() => toggleTab("form")}
          isActive={props.activeTabs.includes("form")}
          onClick={() => props.onToggleTab("form")}
          badgeCount={props.forms?.length || 0}
        />

        {/* Booking Icon */}
        <IconTab
          iconName="calendar"
          label="Booking"
          // isActive={activeTabs.includes("booking")}
          // onClick={() => toggleTab("booking")}
          isActive={props.activeTabs.includes("booking")}
          onClick={() => props.onToggleTab("booking")}
          badgeCount={props.appointments?.length || 0}
        />

        {/* Note Icon */}
        <IconTab
          iconName="sticky note"
          label="Note"
          // isActive={activeTabs.includes("note")}
          // onClick={() => toggleTab("note")}
          isActive={props.activeTabs.includes("note")}
          onClick={() => props.onToggleTab("note")}
          badgeCount={props.notes?.length || 0}
        />

        {/* Cart Icon */}
        <IconTab
          iconName="shop"
          label="Cart"
          isActive={props.activeTabs.includes("cart")}
          onClick={() => props.onToggleTab("cart")}
          badgeCount={props.cartData?.cartCount || 0}
        />
      </div>

      {/* Content Sections */}
      <div style={{ marginTop: "20px" }}>
        {props.activeTabs.map((tab, index) => {
          if (tab === "profile") {
            return (
              <ProfileSection
                key={`profile-${index}`}
                patientData={props.patientData}
                proxyPatient={props.selectedProxyPatient}
                isCollapsed={collapsedSections.personalInfo || false}
                onToggle={() => toggleSection("personalInfo")}
              />
            );
          }

          if (tab === "form") {
            return (
              <FormSection
                key={`form-${index}`}
                forms={props.forms}
                diagFormData={props.diagFormData}
                isCollapsed={collapsedSections.forms || false}
                onToggle={() => toggleSection("forms")}
                onSearchForm={props.onSearchForm}
              />
            );
          }

          if (tab === "booking") {
            return (
              <BookingSection
                key={`booking-${index}`}
                appointments={props.appointments}
                isCollapsed={collapsedSections.bookings || false}
                officialAccountList={props.officialAccountList}
                selectedChat={props.selectedChat}
                onEvent={props.onEvent}
                onToggle={() => toggleSection("bookings")}
                timeDivision={props.timeDivision}
                createAppointment={props.createAppointment}
                loadingStatus={props.loadingStatus}
                errorMessage={props.errorMessage}
                createdAppointment={props.createdAppointment}
                apiToken={props.apiToken}
                setProp={props.setProp}
              />
            );
          }

          if (tab === "note") {
            return (
              <NoteSection
                key={`note-${index}`}
                notes={props.notes}
                isCollapsed={collapsedSections.notes || false}
                onToggle={() => toggleSection("notes")}
                onSaveNote={props.onSaveNote}
                onUpdateNote={props.onUpdateNote}
                onDeleteNote={props.onDeleteNote}
              />
            );
          }

          if (tab === "cart") {
            return (
              <CartSection
                // key ثابت — ไม่งั้นการเปิด/ปิด tab อื่นทำให้ index ย้าย แล้ว CartSection remount
                // (ค่า keyword ที่ staff พิมพ์ไว้หาย + โหลดใหม่ทั้งลิสต์)
                key="cart"
                cartData={props.cartData}
                patientId={props.selectedChat?.patient}
                isCollapsed={collapsedSections.cart || false}
                onToggle={() => toggleSection("cart")}
                isLoading={props.loadingStatus?.[CartPackageListCard]}
                addLoadingStatus={props.loadingStatus}
                errorMessage={props.errorMessage}
                onSearchPackages={(keyword, refresh) =>
                  props.onEvent({
                    message: "GetCartPackageList",
                    params: {
                      patient: props.selectedChat?.patient,
                      keyword,
                      ...(refresh ? { refresh: true } : {}),
                    },
                  })
                }
                onAddPackage={(packageId) =>
                  props.onEvent({
                    message: "HandleAddCartPackage",
                    params: { packageId },
                  })
                }
                onDecreasePackage={(packageId) =>
                  props.onEvent({
                    message: "HandleDecreaseCartPackage",
                    params: { packageId },
                  })
                }
                isSendingPaymentLink={!!props.loadingStatus?.[CartPaymentLinkCard]}
                onSendCartPaymentLink={async () =>
                  props.onSendCartPaymentLink ? props.onSendCartPaymentLink() : null
                }
                onLoadMorePackages={(keyword, offset) =>
                  props.onEvent({
                    message: "GetCartPackageList",
                    params: { patient: props.selectedChat?.patient, keyword, offset },
                  })
                }
              />
            );
          }

          return null;
        })}
      </div>
    </Segment>
  );
};

export default ProfilePanel;
